import { ReadonlyVec3, vec3 } from 'gl-matrix';
import type { Grid } from '~/engine/data/common/Grid';

/**
 * Voxel-space axis-aligned box with INCLUSIVE bounds — the region of a
 * 3D texture an operation writes.
 *
 * Shared by the stroke actions and the renderers they drive so a box is
 * described, clamped, and tested for emptiness one way only: an
 * off-by-one between two spellings of the same box would write (or fail
 * to write) a slice of a structure without any test noticing.
 */
export interface VoxelBox {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
    zMin: number;
    zMax: number;
}

/** Clamp a box to the grid, so no bound addresses a voxel that is not there. */
export function clampBoxToGrid(
    box: VoxelBox,
    sizeVoxels: ReadonlyVec3
): VoxelBox {
    return {
        xMin: Math.max(0, box.xMin),
        xMax: Math.min(sizeVoxels[0] - 1, box.xMax),
        yMin: Math.max(0, box.yMin),
        yMax: Math.min(sizeVoxels[1] - 1, box.yMax),
        zMin: Math.max(0, box.zMin),
        zMax: Math.min(sizeVoxels[2] - 1, box.zMax),
    };
}

/** Whether the box covers no voxels at all (any axis inverted). */
export function isEmptyBox(box: VoxelBox): boolean {
    return box.xMin > box.xMax || box.yMin > box.yMax || box.zMin > box.zMax;
}

/**
 * Intersect a box with the one-slice-thick slab around `centerVoxel`,
 * as seen by a view — how every 2D-mode contouring stroke is confined to
 * the slice under the cursor.
 *
 * The slab is one voxel footprint thick along the view normal
 * ({@link Grid.voxelExtentAlong}) and is bounded per grid axis from the
 * normal's component along that axis, so it is correct for any grid
 * orientation and for anisotropic spacing. A grid axis the view does not
 * travel along (component ≈ 0) is left untouched: the slab imposes no
 * bound there.
 *
 * @param box The unrestricted box, in voxels of `grid`
 * @param centerVoxel Cursor position in voxel space of `grid`
 * @param viewDirection Plane normal of the view (patient space, any
 *     non-zero length)
 * @param grid The grid `box` and `centerVoxel` are expressed in
 */
export function intersectWithSliceSlab(
    box: VoxelBox,
    centerVoxel: ReadonlyVec3,
    viewDirection: ReadonlyVec3,
    grid: Grid
): VoxelBox {
    const halfSpacingMm = 0.5 * grid.voxelExtentAlong(viewDirection);
    const gridDir = grid.rotateToGrid(viewDirection);
    vec3.normalize(gridDir, gridDir);
    const resMmPerVoxel = [grid.resX, grid.resY, grid.resZ];
    const result = { ...box };
    const axes = [
        ['xMin', 'xMax'],
        ['yMin', 'yMax'],
        ['zMin', 'zMax'],
    ] as const;
    for (let axis = 0; axis < 3; axis++) {
        const component = Math.abs(gridDir[axis]);
        if (component <= 1e-6) {
            continue;
        }
        const radiusVoxels = halfSpacingMm / (resMmPerVoxel[axis] * component);
        const [minKey, maxKey] = axes[axis];
        result[minKey] = Math.max(
            result[minKey],
            Math.floor(centerVoxel[axis] - radiusVoxels)
        );
        result[maxKey] = Math.min(
            result[maxKey],
            Math.ceil(centerVoxel[axis] + radiusVoxels)
        );
    }
    return result;
}
